
import { HttpParams } from '@angular/common/http';
import { Component, ContentChildren, ElementRef, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID, QueryList, forwardRef } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import * as _ from 'lodash';
import * as moment from 'moment-timezone';
import * as moment_tz from 'moment-timezone';
import { Subscription } from 'rxjs';
import { ErrorMessages } from '../../common/constants';
import { Metric, MetricValueType, Thing, Value } from '../../model';
import { AuthenticationService } from '../../service/authentication.service';
import { ColorManagerService } from '../../service/color-manager.service';
import { ContextService } from '../../service/context.service';
import { DataService } from '../../service/data.service';
import { DateRangeName, DateRangeService, PeriodVariable } from '../../service/date-range.service';
import { FieldService } from '../../service/field.service';
import { FilterService } from '../../service/filter.service';
import { AbstractThingContextService } from '../../shared/class/abstract-thing-context-service.class';
import { MetricAggregationType, MetricDetailComponent } from '../../shared/component';
import { LoaderPipe, LocalizationPipe } from '../../shared/pipe';
import { Aggregation, DataAggregationOptions, DataAggregationUtility, Interval, Timeframe } from '../../utility/data-aggregation-utility';
import { ErrorUtility } from '../../utility/error-utility';
import { AmChart5Component } from '../amchart5/am-chart5.component';
import { WidgetPrintResizeCoordinatorService } from '../shared/widget-print-resize-coordinator.service';
import { MetricStateComponent, MetricStateSeverity } from './metric-state.component';

@Component({
    selector: 'metric-state-diagram-widget',
    template: require('./metric-state-diagram.component.html'),
    styles: [require('./metric-state-diagram.component.css')]
})
export class MetricStateDiagramComponent extends AmChart5Component implements OnInit, OnDestroy {

    @Input() title: string;

    @Input() metricName: string;

    @Input() predicate: string;

    @Input() value: any;

    @Input() stateBarColor: string;

    @Input() valueFilter: string;

    @Input() description: string;

    @Input() timeframe: Timeframe = Timeframe.NONE;

    @Input() periodRef: string;

    @Input() offlineEnabled: boolean;

    @Input() aggregation: Aggregation = Aggregation.AVG;

    @Input() defaultLoadingPeriod: DateRangeName = DateRangeName.LAST_7_DAYS;

    @Input() showSummaryTable: boolean = true;

    @ContentChildren(MetricStateComponent) metricStates: QueryList<MetricStateComponent>;

    @ContentChildren(MetricDetailComponent) additionalMetrics: QueryList<MetricDetailComponent>;

    metric: Metric;
    chartData: DataBar[];
    selectedCategory: ChartCategory;
    locale: string;
    metricStateArray: MetricStateComponent[];
    timeColumnWidth: number;
    timeLabelTranslation: string;
    metricStateColumnWidthMap: { [metricStateLabel: string]: number } = {};
    additionalMetricColumnWidthMap: { [metricName: string]: number } = {};
    loadedData: boolean;
    additionalMetricArray: MetricDetailComponent[];
    error: string;
    chartMaxHeight: number;
    newStyleManagerEnabled: boolean;

    private categories: ChartCategory[] = [];
    private thing: Thing;
    private timezone: string;
    private series: any;
    private yAxis: any;
    private chart: any;
    private fieldRefTimeoutId: any;
    private fieldSubscription: Subscription;
    private fieldRefTimeoutMillis: number = 500;
    private range: DateRange<moment.Moment>;
    private subscribedFieldNames: string[];
    private fieldsMap: any;
    private firstInitCompleted: boolean;
    private metricStateData: { [category: string]: { [metricStateLabel: string]: string } };
    private offlineMetricState: MetricStateComponent;
    private metrics: Metric[];
    private additionalMetricData: { [category: string]: { [metricName: string]: string } };
    private legend: any;
    private rowHeight: number = 32;
    private extraChartHeight: number = 95.2;
    private chartMinHeight: number = 150;
    private isInitMetricStatesFromValues: boolean;
    private maxVisibleRows: number;
    private _unregisterPrintResize: (() => void) | null = null;

    constructor(
        @Inject(PLATFORM_ID) platformId: Object,
        @Inject(forwardRef(() => NgZone)) zone: NgZone,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => DataService)) private dataService: DataService,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => LoaderPipe)) private loaderPipe: LoaderPipe,
        @Inject(forwardRef(() => ContextService)) private contextService: ContextService,
        @Inject(forwardRef(() => FieldService)) private fieldService: FieldService,
        @Inject(forwardRef(() => DateRangeService)) private dateRangeService: DateRangeService,
        @Inject(forwardRef(() => DataAggregationUtility)) private dataAggregationUtility: DataAggregationUtility,
        @Inject(forwardRef(() => FilterService)) private filterService: FilterService,
        @Inject(forwardRef(() => ColorManagerService)) private colorManagerService: ColorManagerService,
        @Inject(forwardRef(() => ElementRef)) private elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) private widgetPrintResizeCoordinator: WidgetPrintResizeCoordinatorService
    ) {
        super(platformId, zone);
    }

    ngOnInit(): void {
        this.thing = this.thingContextService.getCurrentThing();
        this.timezone = this.contextService.getTimezone();
        this.locale = this.authenticationService.getLocale();
        this.newStyleManagerEnabled = this.authenticationService.isNewStyleManagerEnabled();
        this.thingContextService.getMetricByName(this.metricName).then(metric => this.metric = metric);
        this.thingContextService.getMetrics().then(metrics => this.metrics = metrics);
        this.timeLabelTranslation = this.localizationPipe.transform("Date");
        if (![Timeframe.NONE, Timeframe.MINUTES_15, Timeframe.HOURS_1].includes(this.timeframe)) {
            this.timeframe = Timeframe.NONE;
        }
        this.chartMaxHeight = this.getChartMaxHeight();
        this.maxVisibleRows = Math.floor((this.chartMaxHeight - this.extraChartHeight) / this.rowHeight);
        this._unregisterPrintResize = this.widgetPrintResizeCoordinator.subscribeElement(this.elementRef, () => this.updateBeforePrint());
    }

    private getChartMaxHeight(): number {
        const viewPortHeight = document.documentElement.clientHeight;
        const navbar = document.getElementsByClassName("navbar");
        const navbarHeight = navbar ? navbar[0]?.clientHeight : 0;
        const header = document.getElementsByClassName("content-header");
        const headerHeight = header ? header[0]?.clientHeight : 0;
        const footer = document.getElementsByClassName("main-footer");
        const footerHeight = footer ? footer[0]?.clientHeight : 0;
        const cardExtraHeight = this.title ? 90 : 32;
        return viewPortHeight - navbarHeight - headerHeight - footerHeight - cardExtraHeight;
    }

    getChartId(): string {
        return 'metric-state-diagram';
    }

    initChart(): void {
        // Create chart
        // https://www.amcharts.com/docs/v5/charts/xy-chart/        
        this.chart = this.root.container.children.push(this.am5xy.XYChart.new(this.root, {
            focusable: true,
            panX: false,
            panY: true,
            wheelY: "panY",
            layout: this.root.verticalLayout
        }));

        // Create axes
        // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
        this.yAxis = this.chart.yAxes.push(
            this.am5xy.CategoryAxis.new(this.root, {
                categoryField: "category",
                renderer: this.am5xy.AxisRendererY.new(this.root, {
                    minGridDistance: 1
                }),
                tooltip: this.am5.Tooltip.new(this.root, {
                    themeTags: ["axis"],
                    animationDuration: 200
                }),
                maxDeviation: 0,
                maxZoomCount: this.maxVisibleRows
            })
        );
        // To be restored when implementing the details panel
        // this.yAxis.get("renderer").labels.template.events.on("click", e => {
        //     this.resetLabelStyle(e.target.parent);
        //     e.target.set("fontWeight", "bold");
        //     this.zone.run(() => this.selectedCategory = this.categories.find(c => c.label == e.target.dataItem.dataContext['category']));
        // });
        // this.yAxis.get("renderer").labels.template.events.on("pointerover", e => {
        //     e.target.set("textDecoration", "underline");
        // });
        // this.yAxis.get("renderer").labels.template.events.on("pointerout", e => {
        //     e.target.set("textDecoration", null);
        // });
        if (this.showSummaryTable) {
            this.yAxis.get("renderer").labels.template.adapters.add("html", (html, target) => {
                const data = target.dataItem?.dataContext;
                if (data && this.metricStateData) {
                    let textResult = `<div class="data-table-row">`
                    textResult += `<span style="width: ${this.timeColumnWidth}ch;">${data.category}</span>`
                    this.metricStateArray.forEach(state => {
                        textResult += `<span style="width: ${this.metricStateColumnWidthMap[state.label]}ch;">${this.metricStateData[data.category][state.label] || '-'}</span>`
                    });
                    this.additionalMetricArray.forEach(m => {
                        textResult += `<span style="width: ${this.additionalMetricColumnWidthMap[m.name]}ch;">${this.additionalMetricData[data.category][m.name] || '-'}</span>`
                    });
                    textResult += `</div>`;
                    return textResult;
                } else {
                    return html;
                }
            });
            this.yAxis.get("renderer").labels.template.setAll({
                paddingRight: 15
            });
        } else {

            //Adds legend
            this.legend = this.chart.children.push(this.am5.Legend.new(this.root, {
                nameField: "name",
                fillField: "color",
                strokeField: "color",
                clickTarget: "none"
            }));
        }

        let xAxis = this.chart.xAxes.push(
            this.am5xy.DateAxis.new(this.root, {
                baseInterval: { timeUnit: "minute", count: 1 },
                markUnitChange: false,
                renderer: this.am5xy.AxisRendererX.new(this.root, { strokeOpacity: 0.1 }),
                maxDeviation: 0,
                min: -3600000,
                max: 82799000
            })
        );

        if (this.authenticationService.isNewStyleManagerEnabled()) {
            this.applyStyleManagerTheme(xAxis);
        }

        // Add series
        // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
        this.series = this.chart.series.push(this.am5xy.ColumnSeries.new(this.root, {
            xAxis: xAxis,
            yAxis: this.yAxis,
            openValueXField: "fromDate",
            valueXField: "toDate",
            categoryYField: "category",
            sequencedInterpolation: true
        }));

        this.series.columns.template.setAll({
            templateField: "columnSettings",
            strokeOpacity: 0,
            minWidth: 2,
            tooltipText: this.getTooltipText()
        });

        this.series.data.processor = this.am5.DataProcessor.new(this.root, {
            dateFields: ["fromDate", "toDate"],
            dateFormat: "HH:mm:ss"
        });

        // Add scrollbars
        this.chart.set("scrollbarX", this.am5.Scrollbar.new(this.root, {
            orientation: "horizontal"
        }));
        this.chart.set("scrollbarY", this.am5.Scrollbar.new(this.root, {
            orientation: "vertical",
            visible: false
        }));
        let scrollbarY = this.chart.get("scrollbarY");
        scrollbarY.startGrip.setAll({
            disabled: true,
            visible: false
        });
        scrollbarY.endGrip.setAll({
            disabled: true,
            visible: false
        });
        this.yAxis.events.on("datavalidated", (ev) => {
            if (this.categories.length > this.maxVisibleRows) {
                ev.target.zoomToIndexes(this.categories.length, this.categories.length - this.maxVisibleRows);
                scrollbarY.set("visible", true);
                this.chart.root.dom.style.height = this.chartMaxHeight + "px";
            } else {
                scrollbarY.set("visible", false);
                ev.target.zoomToIndexes(this.categories.length, 0);
                this.chart.root.dom.style.height = Math.max(((this.rowHeight * this.categories.length) + this.extraChartHeight), this.chartMinHeight) + "px";
            }
        });

        // Data
        this.initMetricStateComponents();
        this.initAdditionalMetricComponents();
        if (!this.error) {
            this.subscribeToFields();
        }
    }

    private applyStyleManagerTheme(xAxis: any): void {
        const axesColor = this.getCssColor('--chart-axes-color');
        const axesOpacity = this.getCssOpacity('--chart-axes-opacity');
        const axesLabelColor = this.getCssColor('--chart-axes-label-color');
        const axesLabelOpacity = this.getCssOpacity('--chart-axes-label-opacity');
        const gridColor = this.getCssColor('--chart-grid-color');
        const gridOpacity = this.getCssOpacity('--chart-grid-opacity');

        // The X axis shows the time scale (axes labels); the Y axis labels carry the row data (data labels)
        [xAxis, this.yAxis].forEach(axis => {
            const renderer = axis.get("renderer");
            renderer.setAll(this.definedSettings({ stroke: axesColor, strokeOpacity: axesOpacity }));
            renderer.grid.template.setAll(this.definedSettings({ stroke: gridColor, strokeOpacity: gridOpacity }));
        });
        xAxis.get("renderer").labels.template.setAll(this.definedSettings({ fill: axesLabelColor, fillOpacity: axesLabelOpacity }));
        this.applyDataLabelStyle(this.yAxis.get("renderer").labels.template);

        if (this.legend) {
            this.applyDataLabelStyle(this.legend.labels.template);
        }

        this.applyChartBackground(this.chart);
    }

    private initMetricStateComponents(): void {
        if (!this.metricName) {
            this.error = "Invalid configuration: missing metric name";
        }
        const metric = this.metrics.find(m => m.name == this.metricName);
        if (!metric) {
            this.error = "Invalid configuration: invalid metric";
        }
        this.metricStateArray = [];
        if (this.metricStates?.length) {
            this.metricStates?.forEach(ms => {
                if (ms.label && ms.predicate && (ms.value != null || ms.valueRef)) {
                    const operator = operators[ms.predicate];
                    if (!operator) {
                        this.error = this.localizationPipe.transform("Invalid operator") + " " + ms.predicate;
                    } else {
                        this.metricStateArray.push(ms);
                    }
                } else {
                    this.error = "Invalid metric state configuration (missing label, predicate or value)";
                }
            });
        } else if (this.predicate && this.value != null) {    // creates metricState from deprecated inputs
            let newMetricState = new MetricStateComponent();
            newMetricState.label = this.metricName;
            newMetricState.predicate = this.predicate;
            newMetricState.value = this.value;
            newMetricState.stateBarColor = this.stateBarColor;
            this.metricStateArray.push(newMetricState);
        } else {
            if (metric.dictionary?.length) {
                metric.dictionary.forEach(dictionary => {
                    let newMetricState = new MetricStateComponent();
                    newMetricState.label = dictionary.label;
                    newMetricState.predicate = 'eq';
                    newMetricState.value = dictionary.value;
                    newMetricState.severity = dictionary.severity;
                    this.metricStateArray.push(newMetricState);
                });
            } else if (metric.ranges?.length > 1) {
                metric.ranges.forEach((range, index) => {
                    let newMetricState = new MetricStateComponent();
                    newMetricState.label = range.label;
                    newMetricState.predicate = (index == metric.ranges.length - 1) ? 'gt' : 'le';
                    newMetricState.value = (index == metric.ranges.length - 1) ? metric.ranges[index - 1].to : range.to;
                    newMetricState.severity = range.severity;
                    this.metricStateArray.push(newMetricState);
                });
            } else if (metric.valueType == MetricValueType.STRING) {
                this.isInitMetricStatesFromValues = true;
            }
        }
        this.offlineMetricState = {
            label: 'Offline',
            predicate: 'ne',
            severity: null,
            stateBarColor: '#777777',
            value: 1,
            valueRef: null
        }
    }

    private initAdditionalMetricComponents(): void {
        this.additionalMetricArray = [];
        this.additionalMetrics?.forEach(m => {
            const metric = this.metrics.find(metric => m.name == metric.name);
            m.aggregation = m.aggregation || MetricAggregationType.LAST_VALUE;
            if (!metric) {
                this.error = this.localizationPipe.transform("Invalid metric") + " " + m.name;
            } else if (![MetricAggregationType.AVG, MetricAggregationType.SUM, MetricAggregationType.MIN, MetricAggregationType.MAX, MetricAggregationType.DELTA, MetricAggregationType.LAST_VALUE].includes(m.aggregation)) {
                this.error = this.localizationPipe.transform("Invalid aggregation") + " " + m.aggregation;
            } else if (!this.additionalMetricArray.some(component => component.name == m.name)) {
                m.label = m.label || metric.label || metric.name;
                this.additionalMetricArray.push(m);
            }
        });
    }

    private loadChartData(): void {
        this.loadedData = false;
        if (this.periodRef) {
            const period: PeriodVariable = this.fieldsMap ? this.fieldsMap[this.periodRef] : null;
            if (period) {
                this.range = new DateRange(moment_tz.tz(period.start, this.timezone), moment_tz.tz(period.end, this.timezone));
            }
        }
        if (!this.range) {
            const daterange = this.dateRangeService.getCustomDateRangeByName(this.defaultLoadingPeriod);
            this.range = new DateRange(daterange.range.start, daterange.range.end);
        }
        this.computeCategories();
        this.getData().then(() => {
            this.series.data.setAll(this.chartData || []);
            this.yAxis.data.setAll(this.categories.map(c => { return { category: c.label } }));

            // Make stuff animate on load
            // https://www.amcharts.com/docs/v5/concepts/animations/
            this.series.appear();
            this.chart.appear(1000, 100);
            if (this.legend) {
                this.legend.data.setAll(this.getLegendData());
            }
            this.zone.run(() => this.loadedData = true);
        });
    }

    private getLegendData(): { name: string, color: string }[] {
        return this.metricStateArray.map(state => {
            return { name: this.localizationPipe.transform(this.resolveMetricStatePlaceholder(state.label)), color: this.getBarColorFromMetricState(state) };
        });
    }

    private computeCategories() {
        this.categories = [];
        let numberOfdays = Math.floor(moment_tz.duration(this.range.end.valueOf() - this.range.start.valueOf()).asDays());
        let endTimestamp = this.range.end.valueOf();
        for (let i = numberOfdays; i >= 0; i--) {
            const startDate = moment_tz.tz(endTimestamp, this.timezone).subtract(i, 'day').startOf('day');
            const endDate = moment_tz.tz(endTimestamp, this.timezone).subtract(i, 'day').endOf('day');
            this.categories.push({
                startDate: startDate,
                endDate: endDate,
                label: startDate.locale(this.locale).format('L'),
                extendedLabel: startDate.locale(this.locale).format('dddd DD MMMM YYYY')
            });
        }
    }

    private resetLabelStyle(container: any) {
        for (let elem of container.children.values) {
            if (elem instanceof this.am5xy.AxisLabel) {
                elem.set("fontWeight", "normal");
            }
        }
    }

    private getTooltipText(): string {
        let text;
        switch (this.timeframe) {
            case Timeframe.MINUTES_15:
            case Timeframe.HOURS_1:
                text = "{tooltipConfig.metricStateLabel}\n{tooltipConfig.startLabel}: {openValueX.formatDate('HH:mm:ss')}\n{tooltipConfig.endLabel}: {valueX.formatDate('HH:mm:ss')}";
                break;
            default:
                text = "{tooltipConfig.metricStateLabel}\n{tooltipConfig.startLabel}: {openValueX.formatDate('HH:mm:ss')}\n{tooltipConfig.endLabel}: {valueX.formatDate('HH:mm:ss')}\n{tooltipConfig.durationLabel}: {durationFormatted}";
        }
        return text;
    }

    private getData(): Promise<void> {
        const startTimestamp = this.range.start.valueOf();
        const endTimestamp = this.range.end.valueOf();
        let promises: any = [];
        promises.push(this.dataService.getLastValueByThingIdAndMetricName(this.thing.id, this.metricName, new HttpParams().set('endDate', startTimestamp - 1)));
        promises.push(this.dataService.getValues(this.metricName, this.thing.id, null, new HttpParams().set('startDate', startTimestamp).set('endDate', endTimestamp)));
        if (this.offlineEnabled) {
            promises.push(this.dataService.getLastValueByThingIdAndMetricName(this.thing.id, 'Connection Status', new HttpParams().set('endDate', startTimestamp - 1)));
            promises.push(this.dataService.getValues('Connection Status', this.thing.id, null, new HttpParams().set('startDate', startTimestamp).set('endDate', endTimestamp)))
        }
        this.additionalMetricData = {};
        this.categories.forEach(c => this.additionalMetricData[c.label] = {});
        this.additionalMetricArray.forEach(m => {
            promises.push(this.dataService.getValues(m.name, this.thing.id, null, new HttpParams().set('startDate', startTimestamp).set('endDate', endTimestamp)).then(results => this.computeAdditionalMetricData(results.values, m)));
        });
        return Promise.all(promises).then(resp => {
            let values: Value[] = this.aggregateValues(resp[1].values).reverse();
            const previousValue: Value = resp[0];
            if (!!this.getMetricStateFromValue(previousValue) && (values[0]?.timestamp != startTimestamp)) {
                previousValue.timestamp = startTimestamp;
                values = [previousValue, ...values];
            }
            let connectionStatusValues: Value[] = [];
            if (this.offlineEnabled) {
                connectionStatusValues = resp[3].values.reverse();
                const previousConnectionStatusValue: Value = resp[2];
                if (previousConnectionStatusValue && connectionStatusValues[0]?.timestamp != startTimestamp) {
                    previousConnectionStatusValue.timestamp = startTimestamp;
                    connectionStatusValues = [previousConnectionStatusValue, ...connectionStatusValues];
                }
            }
            if (this.valueFilter) {
                values.forEach(v => {
                    v.value = this.loaderPipe.transform(v.value, this.valueFilter, true, { metric: this.metric });
                });
            }
            if (this.isInitMetricStatesFromValues) {
                this.initMetricStatesFromValues(values);
            }
            this.zone.run(() => {
                let data: DataBar[];
                switch (this.timeframe) {
                    case Timeframe.MINUTES_15:
                    case Timeframe.HOURS_1:
                        data = this.getDiscreteData(values, connectionStatusValues);
                        break;
                    default:
                        const offlineValues = connectionStatusValues.filter(v => this.isValidValue(v, this.offlineMetricState));
                        const allValues: { value: Value, isOfflineValue: boolean }[] = values.map(v => { return { value: v, isOfflineValue: false } }).concat(offlineValues.map(v => { return { value: v, isOfflineValue: true } }));
                        allValues.sort((a, b) => {
                            if (a.value.timestamp != b.value.timestamp) {
                                return a.value.timestamp - b.value.timestamp;
                            }
                            return Number(a.isOfflineValue) - Number(b.isOfflineValue);
                        });
                        if (this.offlineEnabled && allValues.length > 1 && allValues[0].value.timestamp == allValues[1].value.timestamp) {     // prioritize offline value
                            allValues.splice(0, 1);
                        }
                        data = this.getContinuousData(allValues);
                }
                this.computeMetricStateData(data);
                this.computeColumnsWidth();
                this.chartData = data;
            });
        }).catch(err => {
            this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR);
        })
    }

    private getContinuousData(values: { value: Value, isOfflineValue: boolean }[]): DataBar[] {
        let dataBar;
        let categoryIndex = 0;
        let chartData: DataBar[] = [];
        let previousMetricState: MetricStateComponent;
        for (let value of values) {
            const val = value.value;
            let metricState;
            if (value.isOfflineValue) {
                metricState = this.offlineMetricState;
            } else {
                metricState = this.getMetricStateFromValue(val);
            }
            while (val.timestamp > this.categories[categoryIndex].endDate.valueOf()) {
                if (dataBar) {
                    const endMoment = this.categories[categoryIndex].endDate;
                    dataBar.toDate = this.formatHours(endMoment);
                    dataBar.toMoment = endMoment;
                    dataBar.duration = moment_tz.duration(dataBar.toMoment.diff(dataBar.fromMoment));
                    dataBar.durationFormatted = MetricStateDiagramComponent.formatDuration(dataBar.duration);
                    chartData.push(dataBar);
                    dataBar = this.createDataBar(this.categories[categoryIndex + 1].label, this.categories[categoryIndex + 1].startDate, previousMetricState);
                }
                categoryIndex++;
            }
            if (dataBar) {
                const endMoment = moment_tz.tz(val.timestamp - 1, this.timezone);
                dataBar.toDate = this.formatHours(endMoment);
                dataBar.toMoment = endMoment;
                dataBar.duration = moment_tz.duration(dataBar.toMoment.diff(dataBar.fromMoment));
                dataBar.durationFormatted = MetricStateDiagramComponent.formatDuration(dataBar.duration);
                if (!metricState) {
                    chartData.push(dataBar);
                    dataBar = null;
                } else if (!_.isEqual(metricState, previousMetricState)) {
                    chartData.push(dataBar);
                    dataBar = this.createDataBar(this.categories[categoryIndex].label, moment_tz.tz(val.timestamp, this.timezone), metricState);
                }
            } else if (metricState) {
                dataBar = this.createDataBar(this.categories[categoryIndex].label, moment_tz.tz(val.timestamp, this.timezone), metricState);
            }
            previousMetricState = metricState;
        }
        if (dataBar) {
            const categoryEndDate = this.categories[this.categories.length - 1].endDate;
            while (categoryEndDate.isAfter(this.categories[categoryIndex].endDate)) {
                const endMoment = this.categories[categoryIndex].endDate;
                dataBar.toDate = this.formatHours(endMoment);
                dataBar.toMoment = endMoment;
                dataBar.duration = moment_tz.duration(dataBar.toMoment.diff(dataBar.fromMoment));
                dataBar.durationFormatted = MetricStateDiagramComponent.formatDuration(dataBar.duration);
                chartData.push(dataBar);
                dataBar = this.createDataBar(this.categories[categoryIndex + 1].label, this.categories[categoryIndex + 1].startDate, previousMetricState);
                categoryIndex++;
            }
            dataBar.toDate = this.formatHours(this.range.end);
            dataBar.toMoment = this.range.end;
            dataBar.duration = moment_tz.duration(dataBar.toMoment.diff(dataBar.fromMoment));
            dataBar.durationFormatted = MetricStateDiagramComponent.formatDuration(dataBar.duration);
            chartData.push(dataBar);
        }
        return chartData;
    }

    private getDiscreteData(metricValues: Value[], connectionStatusValues: Value[]): DataBar[] {
        let discreteData: { dataBar: DataBar, isOnlineBar: boolean }[] = [];
        const discreteCategories = this.getCategoriesFromTimeframe();
        if (!discreteCategories.length) {
            return [];
        }
        const duration = moment_tz.duration(discreteCategories[0].endDate.diff(discreteCategories[0].startDate)).add(1, 'milliseconds');

        // populate with empty data
        discreteCategories.forEach(cat => {
            const startMoment = cat.startDate;
            const endMoment = cat.endDate;
            let dataBar: DataBar = {
                category: cat.label,
                fromDate: this.formatHours(startMoment),
                fromMoment: startMoment,
                toDate: this.formatHours(endMoment),
                toMoment: endMoment,
                duration: duration,
                durationFormatted: null,
                columnSettings: null,
                tooltipConfig: null,
                metricState: null
            };
            discreteData.push({ dataBar: dataBar, isOnlineBar: this.offlineEnabled ? false : true });
        });

        // populate with metric data
        let discreteDataIndex = 0;
        const dummyOnlineMetricState: MetricStateComponent = {
            label: "DUMMY_ONLINE_STATE",
            predicate: null,
            severity: null,
            stateBarColor: null,
            value: null,
            valueRef: null
        }
        metricValues.forEach(val => {
            const metricState = this.getMetricStateFromValue(val);
            while (val.timestamp > discreteData[discreteDataIndex].dataBar.toMoment.valueOf()) {
                discreteDataIndex++;
            }
            if (metricState) {
                discreteData[discreteDataIndex].dataBar.metricState = metricState;
                discreteData[discreteDataIndex].dataBar.columnSettings = { fill: this.getBarColorFromMetricState(metricState) };
                discreteData[discreteDataIndex].dataBar.tooltipConfig = this.getTooltipConfig(this.localizationPipe.transform(metricState.label));
            } else {
                discreteData[discreteDataIndex].dataBar.metricState = dummyOnlineMetricState;
            }
        });

        // populate with Connection Status data
        discreteDataIndex = 0;
        connectionStatusValues.forEach(val => {
            while (val.timestamp > discreteData[discreteDataIndex].dataBar.toMoment.valueOf()) {
                discreteDataIndex++;
                discreteData[discreteDataIndex].isOnlineBar = discreteData[discreteDataIndex - 1].isOnlineBar;
            }
            if (this.isValidValue(val, this.offlineMetricState)) {
                discreteData[discreteDataIndex].isOnlineBar = false;
            } else {
                discreteData[discreteDataIndex].isOnlineBar = true;
            }

        });

        // fill missing data
        discreteData.forEach((d, index) => {
            if (!d.dataBar.metricState) {
                if (!d.isOnlineBar) {
                    d.dataBar.metricState = this.offlineMetricState;
                    d.dataBar.columnSettings = { fill: this.getBarColorFromMetricState(this.offlineMetricState) };
                    d.dataBar.tooltipConfig = this.getTooltipConfig(this.localizationPipe.transform(this.offlineMetricState.label));
                } else if (index > 0) {
                    const previousData = discreteData[index - 1];
                    if (previousData.isOnlineBar) {
                        d.dataBar.metricState = previousData.dataBar.metricState;
                        d.dataBar.columnSettings = previousData.dataBar.columnSettings;
                        d.dataBar.tooltipConfig = previousData.dataBar.tooltipConfig;
                    }
                }
            }
        });
        return discreteData.filter(d => d.dataBar.metricState && d.dataBar.metricState.label != "DUMMY_ONLINE_STATE").map(d => d.dataBar);
    }

    private getCategoriesFromTimeframe(): ChartCategory[] {
        const fullInterval: Interval = { startTimestamp: this.categories[0].startDate.valueOf(), endTimestamp: this.categories[this.categories.length - 1].endDate.valueOf() };
        let intervals: Interval[] = [];
        switch (this.timeframe) {
            case Timeframe.MINUTES_15:
                intervals = this.dataAggregationUtility.splitIntervals(15, fullInterval, 'minutes', 'minute');
                break;
            case Timeframe.HOURS_1:
                intervals = this.dataAggregationUtility.splitIntervals(1, fullInterval, 'hours', 'hour');
                break;
            default:
                this.error = "Invalid timeframe";
        }
        return intervals.map(i => {
            const startDate = moment_tz.tz(i.startTimestamp, this.timezone);
            return {
                startDate: startDate,
                endDate: moment_tz.tz(i.endTimestamp, this.timezone),
                label: startDate.locale(this.locale).format('L'),
                extendedLabel: startDate.locale(this.locale).format('dddd DD MMMM YYYY')
            }
        });
    }

    static formatDuration(duration: moment_tz.Duration): string {
        return `${duration.hours()}h ${duration.minutes()}m ${duration.seconds()}s`;
    }

    private isValidValue(val: Value, metricState: MetricStateComponent): boolean {
        if (!val) {
            return false;
        }
        const operator = operators[metricState.predicate];
        if (!operator) {
            return false;
        }
        const metricStateValue = (this.fieldsMap && this.fieldsMap[metricState.valueRef] != null && this.fieldsMap[metricState.valueRef] != '' && this.fieldsMap[metricState.valueRef] != 'undefined') ? this.fieldsMap[metricState.valueRef] : metricState.value;
        const value = val.value != null ? (['eq', 'in'].includes(metricState.predicate) ? val.value.toString() : Number(val.value)) : null;
        return operator(value, metricStateValue);
    }

    private getMetricStateFromValue(val: Value): MetricStateComponent {
        if (!val) {
            return null;
        }
        return this.metricStateArray.find(ms => this.isValidValue(val, ms));
    }

    private createDataBar(category: string, startMoment: moment_tz.Moment, metricState: MetricStateComponent): DataBar {
        return {
            category: category,
            fromDate: this.formatHours(startMoment),
            fromMoment: startMoment,
            toDate: null,
            toMoment: null,
            duration: null,
            durationFormatted: null,
            columnSettings: { fill: this.getBarColorFromMetricState(metricState) },
            tooltipConfig: this.getTooltipConfig(metricState.label),
            metricState: metricState
        };
    }

    private getTooltipConfig(metricStateLabel: string): any {
        return {
            startLabel: this.localizationPipe.transform('start'),
            endLabel: this.localizationPipe.transform('end'),
            valueLabel: this.localizationPipe.transform('value'),
            durationLabel: this.localizationPipe.transform('duration'),
            metricStateLabel: this.localizationPipe.transform(this.resolveMetricStatePlaceholder(metricStateLabel))
        };
    }

    private formatHours(moment: moment_tz.Moment): string {
        return moment.locale(this.locale).format('HH:mm:ss');
    }

    closeDetails(): void {
        this.selectedCategory = null;
        this.resetLabelStyle(this.yAxis.labelsContainer);
    }

    private updateBeforePrint(): void {
        this.resizeChart();
    }

    ngOnDestroy(): void {
        this._unregisterPrintResize?.();
        this._unregisterPrintResize = null;
        this.unsubscribeFromFields();
        super.ngOnDestroy();
    }

    private subscribeToFields(): void {
        this.subscribedFieldNames = [];
        this.metricStateArray.forEach(state => {
            if (state.valueRef && !this.subscribedFieldNames.includes(state.valueRef)) {
                this.subscribedFieldNames.push(state.valueRef);
            }
        });
        if (this.periodRef) {
            this.subscribedFieldNames.push(this.periodRef);
        }
        if (this.subscribedFieldNames.length) {
            this.fieldRefTimeoutId = setTimeout(() => {
                this.firstInitCompleted = true;
                this.loadChartData();
            }, this.fieldRefTimeoutMillis);
            this.fieldSubscription = this.fieldService.subscribeToFields(this.subscribedFieldNames).subscribe(fieldsMap => {
                this.fieldsMap = fieldsMap;
                if (this.firstInitCompleted) {
                    this.loadChartData();
                }
            });
        } else {
            this.loadChartData();
        }
    }

    private clearFieldRefTimeout(): void {
        if (this.fieldRefTimeoutId) {
            clearTimeout(this.fieldRefTimeoutId);
            this.fieldRefTimeoutId = null;
        }
    }

    private unsubscribeFromFields(): void {
        if (this.subscribedFieldNames.length) {
            this.fieldService.unsubscribeFromFields(this.subscribedFieldNames);
            this.fieldSubscription.unsubscribe();
        }
        this.clearFieldRefTimeout();
    }

    getBarColorFromMetricState(metricState: MetricStateComponent): string {
        if (metricState.stateBarColor) {
            return this.am5.Color.fromString(metricState.stateBarColor);
        }
        switch (MetricStateSeverity[metricState.severity]) {
            case MetricStateSeverity.NEUTRAL:
                return this.getSeverityColor('--severities-neutral', '#212121');
            case MetricStateSeverity.SUCCESS:
                return this.getSeverityColor('--severities-success', '#0EAE1C');
            case MetricStateSeverity.WARNING:
                return this.getSeverityColor('--severities-warning', '#F57B00');
            case MetricStateSeverity.CRITICAL:
                return this.getSeverityColor('--severities-critical', '#F80A0A');
            default:
                return this.getSeverityColor('--severities-neutral', null) || this.chart.get("colors").getIndex(0);
        }
    }

    // This method is called on every change detection (header template), so the CSS lookup is cached
    private severityColorCache: { [variable: string]: string } = {};

    private getSeverityColor(variable: string, fallback: string): string {
        if (!this.newStyleManagerEnabled) {
            return fallback;
        }
        if (!(variable in this.severityColorCache)) {
            const value = getComputedStyle(this.elementRef.nativeElement).getPropertyValue(variable).trim();
            this.severityColorCache[variable] = value || fallback;
        }
        return this.severityColorCache[variable];
    }

    private computeMetricStateData(data: DataBar[]): void {
        let dataMap: { [category: string]: { [metricStateName: string]: { duration: moment.Duration } } } = {};
        this.categories.forEach(c => dataMap[c.label] = {});
        data.forEach(d => {
            const dataDurationMillis = d.duration.asMilliseconds();
            let existingValue = dataMap[d.category][d.metricState.label];
            if (existingValue != null) {
                const existingDurationMillis = existingValue.duration.asMilliseconds();
                existingValue.duration = moment_tz.duration(existingDurationMillis + dataDurationMillis, 'milliseconds');
            } else {
                dataMap[d.category][d.metricState.label] = { duration: moment_tz.duration(dataDurationMillis, 'milliseconds') };
            }
        });
        let result: { [category: string]: { [metricStateName: string]: string } } = {};
        Object.keys(dataMap).forEach(cat => {
            result[cat] = {};
            Object.keys(dataMap[cat]).forEach(metricStateName => {
                const hours = Math.floor(dataMap[cat][metricStateName].duration.asHours());
                const minutes = this.timeframe == Timeframe.HOURS_1 ? 0 : dataMap[cat][metricStateName].duration.minutes();
                result[cat][metricStateName] = ((hours > 0 ? (hours + 'h') : '') + ' ' + (minutes > 0 ? (minutes + 'm') : '')).trim();
            });
        });
        this.metricStateData = result;
    }

    private computeAdditionalMetricData(values: Value[], metricDetailComponent: MetricDetailComponent): void {
        const metric = this.metrics.find(metric => metricDetailComponent.name == metric.name);
        const filter = metricDetailComponent.filter;
        const unit = !this.filterService.isUnitAware(filter) ? (metricDetailComponent.unit || metric.unit) : null;
        if (metricDetailComponent.aggregation == MetricAggregationType.LAST_VALUE) {
            let categoryIndex = 0;
            values.reverse().forEach(v => {
                while (v.timestamp > this.categories[categoryIndex].endDate.valueOf()) {
                    categoryIndex++;
                }
                if (filter) {
                    v.value = this.loaderPipe.transform(v.value, filter, true, { metric: metric });
                }
                this.additionalMetricData[this.categories[categoryIndex].label][metricDetailComponent.name] = v.value.toString() + (unit ? (' ' + unit) : '');
            });
        } else {
            const dataAggregationOptions: DataAggregationOptions = {
                timestampFieldName: null,
                endTimestampFieldName: null,
                valueFieldName: null,
                aggregateFunction: Aggregation[metricDetailComponent.aggregation.valueOf()],
                timeframe: Timeframe.DAYS_1,
                startTimestamp: this.range.start.valueOf(),
                endTimestamp: this.range.end.valueOf()
            }
            const aggregatedValues: Value[] = this.dataAggregationUtility.aggregateData(values, dataAggregationOptions);
            aggregatedValues.forEach(v => {
                if (!Number.isInteger(v.value)) {
                    v.value = v.value.toFixed(2);
                }
                const categoryLabel = moment_tz.tz(v.timestamp, this.timezone).locale(this.locale).format('L');
                if (filter) {
                    v.value = this.loaderPipe.transform(v.value, filter, true, { metric: metric });
                }
                this.additionalMetricData[categoryLabel][metricDetailComponent.name] = v.value.toString() + (unit ? (' ' + unit) : '');
            });
        }
    }

    private computeColumnsWidth(): void {
        this.metricStateColumnWidthMap = {};
        this.metricStateArray.forEach(state => {
            this.metricStateColumnWidthMap[state.label] = this.localizationPipe.transform(this.resolveMetricStatePlaceholder(state.label)).length;
        });
        Object.keys(this.metricStateData).forEach(cat => {
            Object.keys(this.metricStateData[cat]).forEach(metricStateLabel => {
                this.metricStateColumnWidthMap[metricStateLabel] = Math.max(this.metricStateData[cat][metricStateLabel].length, this.metricStateColumnWidthMap[metricStateLabel]);
            });
        });
        this.timeColumnWidth = Math.max(this.categories[0].label.length, this.timeLabelTranslation.length);
        this.additionalMetricColumnWidthMap = {};
        this.additionalMetricArray?.forEach(m => {
            this.additionalMetricColumnWidthMap[m.name] = this.localizationPipe.transform(m.label).length;
        });
        Object.keys(this.additionalMetricData).forEach(cat => {
            Object.keys(this.additionalMetricData[cat]).forEach(metricName => {
                this.additionalMetricColumnWidthMap[metricName] = Math.max(this.extractTextFromHtml(this.additionalMetricData[cat][metricName]).length, this.additionalMetricColumnWidthMap[metricName]);
            });
        });
    }

    private extractTextFromHtml(html): string {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || '';
    }

    resolveMetricStatePlaceholder(metricStateLabel: string): string {
        const metricState = this.metricStateArray.find(ms => ms.label == metricStateLabel);
        if (metricState) {
            const value = (this.fieldsMap && this.fieldsMap[metricState.valueRef] != null && this.fieldsMap[metricState.valueRef] != '' && this.fieldsMap[metricState.valueRef] != 'undefined') ? this.fieldsMap[metricState.valueRef] : metricState.value;
            return metricStateLabel.replace("${value}", value);
        } else {
            return metricStateLabel.replace("${value}", "");
        }
    }

    private aggregateValues(values: Value[]): Value[] {
        switch (this.timeframe) {
            case Timeframe.MINUTES_15:
            case Timeframe.HOURS_1:
                const dataAggregationOptions: DataAggregationOptions = {
                    timestampFieldName: null,
                    endTimestampFieldName: null,
                    valueFieldName: null,
                    aggregateFunction: this.aggregation,
                    timeframe: this.timeframe,
                    startTimestamp: this.range.start.valueOf(),
                    endTimestamp: this.range.end.valueOf()
                }
                return this.dataAggregationUtility.aggregateData(values, dataAggregationOptions);
            default:
                return values;
        }
    }

    private initMetricStatesFromValues(values: Value[]): void {
        this.metricStateArray = [];
        values.forEach(value => {
            if (!this.metricStateArray.some(state => state.value == value.value)) {
                let newMetricState = new MetricStateComponent();
                newMetricState.label = value.value;
                newMetricState.predicate = 'eq';
                newMetricState.value = value.value;
                newMetricState.stateBarColor = this.colorManagerService.getColor(value.value);
                this.metricStateArray.push(newMetricState);
            }
        });
    }
}

const operators: { [key: string]: (a: any, b: any) => boolean } = {
    'lt': (a: any, b: any) => a < b,
    'le': (a: any, b: any) => a <= b,
    'eq': (a: any, b: any) => a == b,
    'ne': (a: any, b: any) => a != b,
    'ge': (a: any, b: any) => a >= b,
    'gt': (a: any, b: any) => a > b,
    'in': (a: any, b: any) => b.split(',').map(el => el.trim()).includes(a)
}

export class ChartCategory {
    startDate: moment_tz.Moment;
    endDate: moment_tz.Moment;
    label: string;
    extendedLabel: string;
}

export class DataBar {
    category: string;
    fromDate: string;
    fromMoment: moment_tz.Moment;
    toDate: string;
    toMoment: moment_tz.Moment;
    duration: moment.Duration;
    durationFormatted: string;
    columnSettings: any;
    tooltipConfig: any;
    metricState: MetricStateComponent;
}